import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, PaperPlaneRight, ArrowClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import StateCheckboxSelector from "@/components/shared/StateCheckboxSelector";

/**
 * Iter-72 — Buyer-facing modal for proposing intake_criteria changes.
 *
 * Buyers are read-only accounts (Iter-30), so they can't PATCH their org
 * directly. Instead this modal collects a structured proposal + a
 * required "why" note, submits it to `POST /api/buyer/criteria-requests`,
 * and closes. The admin will see a diff panel on `/clients/:id/manage`
 * and decide whether to approve (auto-applies) or reject with notes.
 *
 * Design constraint: mirror the field vocabulary of ManageBuyer.jsx so
 * the buyer sees the same options the admin would use — no divergence.
 */

const CASE_TYPES = [
  { code: "auto",                  label: "Auto Accident" },
  { code: "truck",                 label: "Truck Accident" },
  { code: "motorcycle",            label: "Motorcycle Accident" },
  { code: "rideshare",             label: "Rideshare Accident" },
  { code: "public_transportation", label: "Public Transportation" },
  { code: "commercial_vehicle",    label: "Commercial Vehicle" },
  { code: "pedestrian",            label: "Pedestrian" },
  { code: "bicycle",               label: "Bicycle" },
  { code: "other",                 label: "Other Personal Injury" },
];

// Iter-75.6 — "Max incident age" and "Accepted fault levels" removed
// from the buyer-facing request form per user directive. Buyers still
// can't change them; if they were set previously, the values stay
// intact because the modal seeds `proposed` from `currentCriteria` on
// open and only the visible fields can diverge.

const ATTORNEY_OPTIONS = [
  { value: "",               label: "No change / no requirement" },
  { value: "must_be_no",     label: "Must be No (not represented)" },
  { value: "allowed",        label: "Allowed (either OK)" },
];

function Chip({ children, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={
        "px-3 py-1.5 text-[11px] uppercase tracking-widest border-2 min-h-[36px] " +
        (active
          ? "border-black bg-black text-white"
          : "border-zinc-300 hover:border-black")
      }
    >
      {children}
    </button>
  );
}

export default function BuyerCriteriaRequestModal({
  open, onClose, currentCriteria, onSubmitted,
}) {
  const [proposed, setProposed] = useState({});
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset the form every time the modal opens so a previous edit doesn't
  // ghost into a new session.
  useEffect(() => {
    if (open) {
      setProposed(currentCriteria ? { ...currentCriteria } : {});
      setNotes("");
    }
  }, [open, currentCriteria]);

  const set = (key, value) => {
    setProposed((c) => {
      const next = { ...c };
      if (value === "" || value == null
          || (Array.isArray(value) && value.length === 0)) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const toggle = (key, code) => {
    const cur = new Set(proposed[key] || []);
    if (cur.has(code)) cur.delete(code); else cur.add(code);
    set(key, Array.from(cur).sort());
  };

  const resetToCurrent = () => {
    setProposed(currentCriteria ? { ...currentCriteria } : {});
  };

  const hasChanges = useMemo(() => {
    // Compare stable JSON — order-agnostic isn't needed because our
    // toggle handler already sorts multi-select lists.
    return JSON.stringify(proposed || {}) !== JSON.stringify(currentCriteria || {});
  }, [proposed, currentCriteria]);

  const submit = async () => {
    if (!notes.trim()) {
      toast.error("Add a note explaining what you'd like changed and why.");
      return;
    }
    if (!hasChanges) {
      toast.error("Your proposal matches your current criteria — nothing to request.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/buyer/criteria-requests", {
        proposed_criteria: proposed,
        notes: notes.trim(),
      });
      toast.success("Request submitted — your admin will review it.");
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : detail?.message || JSON.stringify(detail) || "Submit failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const attorneyValue = proposed.attorney_policy || "";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 md:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="criteria-request-modal"
    >
      <div className="brut-card bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white p-4 border-b-4 border-black flex items-start justify-between gap-3">
          <div>
            <div className="label-mono">Request criteria change</div>
            <h2 className="font-chivo font-black text-lg md:text-xl uppercase tracking-tighter">
              Propose new intake criteria
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="criteria-request-close"
            className="border border-black px-2 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 hover:bg-black hover:text-white"
          >
            <X size={10} weight="bold" /> Close
          </button>
        </div>

        <div className="p-4 space-y-5">
          <p className="text-xs text-zinc-600 leading-relaxed">
            Adjust the values below to match what you'd like to see. Your admin
            will review your proposal alongside a diff against your current
            settings and either approve or reject with feedback. <b>Nothing
            changes until an admin approves.</b>
          </p>

          {/* Geographic */}
          <div className="brut-card p-3">
            <div className="label-mono mb-2">Accepted states</div>
            <div className="text-[11px] text-zinc-600 mb-2">
              Which US states you'd like leads from. Empty = every state (subject to your destination overrides).
            </div>
            <StateCheckboxSelector
              testId="req-state-selector"
              value={proposed.accepted_states || []}
              onChange={(next) => set("accepted_states", next)}
            />
            {!(proposed.accepted_states || []).length && (
              <div className="mt-2 text-[11px] font-semibold" data-testid="req-states-all-note">
                None ticked: you take leads from every state. Tick states only if you want fewer.
              </div>
            )}
          </div>

          {/* Case types */}
          <div className="brut-card p-3">
            <div className="label-mono mb-2">Case types</div>
            <div className="flex flex-wrap gap-1.5">
              {CASE_TYPES.map((c) => (
                <Chip
                  key={c.code}
                  active={(proposed.case_types || []).includes(c.code)}
                  onClick={() => toggle("case_types", c.code)}
                  testId={`req-case-${c.code}`}
                >
                  {c.label}
                </Chip>
              ))}
            </div>
          </div>

          {/* Attorney policy (Iter-75.6: incident-age + fault-levels removed) */}
          <div className="brut-card p-3">
            <div className="label-mono mb-2">Attorney policy</div>
            <select
              value={attorneyValue}
              data-testid="req-attorney"
              onChange={(e) => set("attorney_policy", e.target.value)}
              className="w-full border border-black px-3 py-2"
            >
              {ATTORNEY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Notes — required */}
          <div className="brut-card p-3">
            <div className="label-mono mb-2">
              Notes <span className="text-rose-600">*</span>
            </div>
            <div className="text-[11px] text-zinc-600 mb-2">
              Explain what you'd like changed and why — this is what your admin
              actually reads. Required.
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Please add FL and GA — we've expanded our practice to the Southeast. Also increase incident age to 6 months, we're finding fresh cases hard to source in Q1."
              rows={4}
              data-testid="req-notes"
              className="w-full border border-black p-2 text-sm font-mono"
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t-4 border-black p-3 flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={resetToCurrent}
            data-testid="req-reset-to-current"
            className="border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 hover:bg-black hover:text-white"
          >
            <ArrowClockwise size={10} weight="bold" /> Reset to current
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
              {hasChanges ? "You have unsaved changes" : "No changes proposed"}
            </span>
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="req-submit"
              className="bg-black text-white px-4 py-2 text-xs uppercase tracking-widest border border-black inline-flex items-center gap-2 disabled:opacity-40"
            >
              <PaperPlaneRight size={12} weight="bold" />
              {submitting ? "Submitting…" : "Submit request"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
